import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueries, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DataTable,
  StatusChip,
  EmptyState,
  SkeletonLoader,
  type DataTableColumn,
} from '@shared/design-system/components';
import {
  bulkSetIssueState,
  getSupportBusHealth,
  getSupportLogs,
  getSupportSiteHealth,
  listRunbookRuns,
  listSupportComponents,
  listSupportIssues,
  listSupportRunbooks,
  listSupportSites,
  setIssueTriageState,
  SupportApiError,
  type SupportAnalysisResponse,
  type SupportComponentSummary,
  type SupportIssuesResponse,
  type SupportLogsResponse,
  type SupportSiteHealth,
  type TriageState,
  type SupportIssueOut,
} from '../../api/supportClient';
import { getRuntimeConfig } from '../../config/runtime-config';
import { grafanaExploreUrl, logPresetsForIssue } from './log-presets';
import { AnalysisJobView, RunLink } from '../../components/AnalysisResultView';
import { useAnalysisJob, type AnalysisJobHandle } from '../../components/use-analysis-job';
import { HeaderFilterSelect } from '../../components/HeaderFilter';
import { KnowledgePreview } from '../../components/KnowledgePreview';
import './DiagnosePane.css';

function severityToStatus(severity: string): 'error' | 'warning' | 'active' {
  if (severity === 'critical' || severity === 'error') return 'error';
  if (severity === 'warning') return 'warning';
  return 'active';
}

const TRIAGE_LABEL: Record<TriageState, string> = {
  open: 'Open',
  in_progress: 'In progress',
  resolved: 'Closed',
};

const TRIAGE_STATES: TriageState[] = ['open', 'in_progress', 'resolved'];

function triageChipKind(state: TriageState): 'completed' | 'warning' | 'error' {
  return state === 'resolved' ? 'completed' : state === 'in_progress' ? 'warning' : 'error';
}

/** Page size for the issue list. Paging is cursor-driven (see
 *  `Load older issues`), so this is a page size and no longer the hard
 *  ceiling it used to be. */
const PAGE_LIMIT = 100;

/** Auto-refresh cadence for the issue list, matching InventoryPane's
 *  health poll. Stated in the UI next to the last-refresh time --
 *  a list that silently stopped refreshing is indistinguishable from a
 *  quiet fleet otherwise. */
const REFRESH_MS = 15_000;

/** Query-key discriminator for the newest page. It is the only page
 *  that is polled, so it needs a key of its own that no cursor can
 *  ever collide with. */
const HEAD_PAGE_KEY = '__head__';

/** Ceiling on the head page's reconciliation walk (see the head query
 *  in `DiagnosePane`). A refresh that has to walk more than this many
 *  pages before it meets a row it already holds is not closing a
 *  boundary band any more, it is re-reading the whole list; stop and
 *  let the next tick try again rather than stalling the UI. */
const MAX_RECONCILE_PAGES = 10;

function componentStatusToChip(
  status: SupportComponentSummary['status'],
): 'active' | 'warning' | 'error' {
  if (status === 'healthy') return 'active';
  if (status === 'degraded') return 'warning';
  return 'error';
}

/** Error text for a failed site-health query. A `SupportApiError`
 *  carries the HTTP status and the api-server's own message, which is
 *  what tells an engineer whether to look at the platform tier or the
 *  site; "query failed" on its own told them nothing. */
function describeQueryError(err: unknown): string {
  if (err instanceof SupportApiError) {
    const head = `HTTP ${err.status}${err.code ? ` ${err.code}` : ''}`;
    // `request()` falls back to "HTTP <status>" as the message when
    // the error body is not JSON; don't print that twice.
    return err.message && err.message !== `HTTP ${err.status}` ? `${head}: ${err.message}` : head;
  }
  return String(err);
}

/** Every route into Pane 3 from here. `runbook` is the addition that
 *  makes a suggestion actionable in one hop: ActPane pins that card to
 *  the top and opens its confirm form with site and reason already
 *  filled in, instead of dropping the engineer at the top of a
 *  catalogue to find the name they just read. Omitting it is the
 *  no-runbook fallback ("Act on this issue"), which still works. */
function actHref(issue: SupportIssueOut, runbookId?: string | null): string {
  const qs = new URLSearchParams();
  if (issue.site) qs.set('site', issue.site);
  qs.set('issue', issue.id);
  if (runbookId) qs.set('runbook', runbookId);
  // THE SIGNATURE THE INCIDENT ACTUALLY HAD, carried across so the
  // outcome is filed under it. Without this Act sends the RUNBOOK's own
  // `trigger_signature`, which is wrong exactly when `also_triggered_by`
  // made the run possible: a transport wedge presenting as
  // `robot_dropped` runs zenoh-routing-recovery, and the outcome plus
  // any auto-drafted lesson land under `adapter_down`. The next
  // `robot_dropped` then asks `recovery_history("robot_dropped")`, gets
  // "no recorded runbook runs for this signature yet", and re-recommends
  // the ordered restart that just failed -- a loop that cannot converge
  // because the secondary-trigger bucket stays permanently empty.
  if (issue.derived_signature) qs.set('signature', issue.derived_signature);
  return `/act?${qs.toString()}`;
}

/** The other two panes that take a `?site=` handoff. AuditPane and
 *  TicketsPane both read that parameter on mount, and until these
 *  links existed nothing in the app ever wrote one -- the reads were
 *  plumbing with no source. History is the natural home: "has this
 *  been seen before, and what has already been done here" is exactly
 *  the question the audit trail and the user-reported tickets answer
 *  next. */
function siteHandoffHref(pane: 'audit' | 'tickets', site: string): string {
  return `/${pane}?site=${encodeURIComponent(site)}`;
}

/** Broker cpu/mem/disk for one site. The three numbers exist only
 *  when the platform tier could reach the site's broker (`status ===
 *  'ok'`); for any other status they would all be null, and
 *  "CPU — · Mem — · Disk —" reads as a broken sample rather than as
 *  the unreachable broker it actually is. */
function SiteResourceLine({ row }: { row: SupportSiteHealth }) {
  const pct = (v: number | null) => (v != null ? `${v}%` : '—');
  if (row.status === 'ok') {
    const noNumbers = row.cpu_percent == null && row.mem_percent == null && row.disk_percent == null;
    const rollup =
      row.containers_total != null && row.containers_ok != null
        ? `${row.containers_ok}/${row.containers_total} containers running and healthy`
        : null;
    const notOk = row.not_ok && row.not_ok.length > 0 ? row.not_ok.join(', ') : null;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 13 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <StatusChip status={notOk ? 'warning' : 'active'} label={notOk ? 'degraded' : 'ok'} />
          {rollup && <span>{rollup}</span>}
          {notOk && <code style={{ fontSize: 12 }}>not ok: {notOk}</code>}
          {/* The three percentages: shown when any exist, otherwise the
              honest sentence. Every Kubernetes site lands in the second
              branch by construction -- its broker reports no cpu/mem/disk
              -- and rendering "CPU — · Mem — · Disk —" there is the
              em-dash regression 0.2.4 removed. */}
          {!noNumbers ? (
            <span style={{ color: 'var(--nx-muted)' }}>
              CPU {pct(row.cpu_percent)} · Mem {pct(row.mem_percent)} · Disk {pct(row.disk_percent)}
            </span>
          ) : (
            <span style={{ color: 'var(--nx-muted)' }}>
              {row.source === 'worker'
                ? 'reached through the site worker; CPU/memory/disk are not reported by the Kubernetes broker'
                : 'no CPU/memory/disk sample yet'}
            </span>
          )}
        </div>
      </div>
    );
  }
  const unreachable = row.status === 'unreachable';
  const workerDown = row.status === 'worker_unreachable';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 13 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <StatusChip
          status={unreachable || workerDown ? 'error' : 'offline'}
          label={workerDown ? 'worker unreachable' : unreachable ? 'unreachable' : 'not configured'}
        />
        <span>
          {workerDown
            ? 'The site worker did not answer the health command — the site or the platform bus, not its broker'
            : unreachable
              ? row.source === 'worker'
                ? 'The site worker reached out to its broker and got no usable answer — the broker pod, not the network'
                : 'Site broker unreachable from the platform tier — CPU/memory/disk are not available here'
              : 'No broker endpoint configured for this site — CPU/memory/disk are not available here'}
        </span>
      </div>
      {row.reason && (
        <code style={{ fontSize: 12, color: 'var(--nx-muted)' }}>{row.reason}</code>
      )}
    </div>
  );
}

/** Same click-to-copy reference chip as the operator SPA's Support tab
 *  (`src/screens/Support/SupportScreen.tsx`) -- duplicated rather than
 *  shared because this app deliberately does not share screen
 *  components with the operator tier, only the design system. Degrades
 *  to plain text where `navigator.clipboard` is unavailable. */
function IssueReference({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  const canCopy = typeof navigator !== 'undefined' && Boolean(navigator.clipboard);

  const copy = useCallback(() => {
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  }, [value]);

  if (!canCopy) {
    return <code style={{ fontSize: 12, color: 'var(--nx-cyan)' }}>{value}</code>;
  }

  return (
    <button
      type="button"
      onClick={copy}
      title={copied ? 'Copied' : 'Copy reference'}
      aria-label={`Copy reference ${value}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        background: 'none',
        border: 0,
        padding: 0,
        cursor: 'pointer',
        color: 'inherit',
      }}
    >
      <code style={{ fontSize: 12, color: 'var(--nx-cyan)' }}>{value}</code>
      <span aria-hidden="true" style={{ fontSize: 11, color: 'var(--nx-muted)' }}>
        {copied ? '✓' : '⧉'}
      </span>
    </button>
  );
}

/**
 * The three triage states as one segmented control, in the pinned
 * header. Previously two verb-named buttons ("Start work" / "Close")
 * whose available set changed with the current state, which meant the
 * control moved under the cursor as you used it and never showed the
 * whole state machine at once. Now all three are always present, the
 * current one is pressed and inert, and the header renders the state
 * chip beside them.
 */
function TriageControl({ issue }: { issue: SupportIssueOut }) {
  const qc = useQueryClient();
  const [note, setNote] = useState('');

  const mutation = useMutation({
    mutationFn: (state: TriageState) => setIssueTriageState(issue.id, state, note),
    onSuccess: () => {
      setNote('');
      // The panel is DERIVED from this query now, so the refetch this
      // kicks off is what repaints the header -- no re-click needed.
      void qc.invalidateQueries({ queryKey: ['support', 'issues'] });
    },
  });

  const current = issue.triage_state;

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <div role="group" aria-label="Triage state" style={{ display: 'flex', gap: 4 }}>
        {TRIAGE_STATES.map((s) => (
          <button
            key={s}
            type="button"
            data-testid={`triage-set-${s}`}
            aria-pressed={s === current}
            disabled={s === current || mutation.isPending}
            title={
              s === current
                ? `Already ${TRIAGE_LABEL[s].toLowerCase()}`
                : `Set this issue to ${TRIAGE_LABEL[s].toLowerCase()}`
            }
            onClick={() => mutation.mutate(s)}
            style={{
              fontSize: 12,
              padding: '3px 10px',
              borderRadius: 'var(--nx-r)',
              border: '1px solid var(--nx-border)',
              background: s === current ? 'var(--nx-bg3)' : 'transparent',
              color: s === current ? 'var(--nx-white)' : 'var(--nx-muted)',
              cursor: s === current ? 'default' : 'pointer',
            }}
          >
            {TRIAGE_LABEL[s]}
          </button>
        ))}
      </div>
      <input
        placeholder="Note (optional)"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        aria-label="Triage note"
        style={{ flex: '1 1 160px', minWidth: 120, padding: 4, fontSize: 12 }}
      />
      <span style={{ fontSize: 11, color: 'var(--nx-muted)' }}>
        {issue.triage_updated_at_ms
          ? `updated ${new Date(issue.triage_updated_at_ms).toLocaleString()}`
          : 'never touched'}
        {current === 'in_progress' && issue.started_by_email
          ? ` · picked up by ${issue.started_by_email}`
          : ''}
        {current === 'resolved' && issue.resolved_by_email
          ? ` · closed by ${issue.resolved_by_email}`
          : ''}
      </span>
      {mutation.isError && (
        <span style={{ color: 'var(--nx-error)', fontSize: 12 }}>{String(mutation.error)}</span>
      )}
    </div>
  );
}

/**
 * Site health for ONE site: the platform-bus observer (the only place
 * `worker ready + silent` — the runtime wedge signature — is visible),
 * per-site component liveness, and the broker cpu/mem/disk fan-out.
 * All three are cross-site responses filtered client-side to this
 * site. First on the Overview tab because "is the site alive" is the
 * question that decides whether the issue text is even worth reading.
 */
function SiteHealthSection({ site }: { site: string }) {
  const busQuery = useQuery({ queryKey: ['support', 'bus'], queryFn: getSupportBusHealth });
  const componentsQuery = useQuery({
    queryKey: ['support', 'components', site],
    queryFn: () => listSupportComponents(site),
  });
  const siteHealthQuery = useQuery({
    queryKey: ['support', 'site-health'],
    queryFn: getSupportSiteHealth,
  });

  const busRow = (busQuery.data?.sites ?? []).find((s) => s.site === site) ?? null;
  const siteHealthRow = (siteHealthQuery.data?.sites ?? []).find((s) => s.site === site) ?? null;

  return (
    <section data-testid="diagnose-site-health">
      <h4 style={{ marginBottom: 6, marginTop: 0 }}>Site health — {site}</h4>
      {busQuery.isLoading || componentsQuery.isLoading || siteHealthQuery.isLoading ? (
        <SkeletonLoader shape="text-line" count={3} />
      ) : busQuery.isError && componentsQuery.isError && siteHealthQuery.isError ? (
        <EmptyState
          title="Site health unavailable"
          description="All three health queries failed for this site."
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {busQuery.isError ? (
            // This endpoint reads the api-server's own in-process
            // bus observer and never contacts the site, so a
            // failure here is a platform-tier fault; the status
            // and message say where to look.
            <p style={{ color: 'var(--nx-error)', margin: 0, fontSize: 13 }}>
              Platform-bus health unavailable — the api-server's bus query failed (
              {describeQueryError(busQuery.error)}). That is a platform-tier fault, not the
              site's: retry, and if it persists check the support api-server logs for{' '}
              <code>/api/v1/support/bus</code>.
            </p>
          ) : busRow ? (
            <>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 13 }}>
                <StatusChip
                  status={busRow.silent ? 'warning' : 'active'}
                  label={busRow.silent ? 'silent' : 'publishing'}
                />
                <span>
                  {busRow.total_msgs_per_s.toFixed(2)} msg/s over a {Math.round(busRow.window_s)}s
                  window
                </span>
                {busRow.last_seen_age_s != null && (
                  <span style={{ color: 'var(--nx-muted)' }}>
                    last message {Math.round(busRow.last_seen_age_s)}s ago
                  </span>
                )}
                <span style={{ color: 'var(--nx-muted)' }}>
                  worker {busRow.worker_status ?? 'unknown'}
                  {busRow.worker_epoch != null ? ` · epoch ${busRow.worker_epoch}` : ''}
                </span>
              </div>
              {busRow.worker_status === 'ready' && busRow.silent && (
                // The one combination that means a WEDGE rather
                // than an outage: the worker's own heartbeat timer
                // keeps reporting ready while the RMF side
                // publishes nothing. Before this section existed
                // it surfaced only as tasks dying at auction.
                <p
                  data-testid="diagnose-bus-wedge"
                  style={{
                    color: 'var(--nx-warning)',
                    border: '1px solid var(--nx-warning)',
                    borderRadius: 'var(--nx-r)',
                    padding: '6px 10px',
                    fontSize: 13,
                    margin: 0,
                  }}
                >
                  Worker reports ready but the site publishes nothing: platform-bus wedge
                  signature (see no_bids knowledge).
                </p>
              )}
            </>
          ) : (
            <p style={{ color: 'var(--nx-muted)', margin: 0 }}>
              No platform-bus row for this site.
              {busQuery.data?.note ? ` (${busQuery.data.note})` : ''}
            </p>
          )}

          {componentsQuery.isError ? (
            <p style={{ color: 'var(--nx-error)', margin: 0 }}>
              Component liveness unavailable ({describeQueryError(componentsQuery.error)}).
            </p>
          ) : (componentsQuery.data ?? []).length === 0 ? (
            <p style={{ color: 'var(--nx-muted)', margin: 0 }}>No components reported.</p>
          ) : (
            <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', fontSize: 13 }}>
              {(componentsQuery.data ?? []).map((c) => (
                <li
                  key={c.component}
                  style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 2 }}
                >
                  <StatusChip status={componentStatusToChip(c.status)} label={c.status} />
                  <span>{c.component}</span>
                  {c.detail && <span style={{ color: 'var(--nx-muted)' }}>{c.detail}</span>}
                </li>
              ))}
            </ul>
          )}

          {siteHealthQuery.isError ? (
            <p style={{ color: 'var(--nx-error)', margin: 0 }}>
              Resource usage unavailable ({describeQueryError(siteHealthQuery.error)}).
            </p>
          ) : siteHealthRow ? (
            <SiteResourceLine row={siteHealthRow} />
          ) : (
            <p style={{ color: 'var(--nx-muted)', margin: 0 }}>
              No resource sample for this site.
            </p>
          )}
        </div>
      )}
    </section>
  );
}

/** Overview: the incident at a glance, in the order an engineer
 *  actually reads it — is the site alive, what does the assistant
 *  think, what could I run. */
function OverviewTab({
  issue,
  ai,
  matchingRunbooks,
}: {
  issue: SupportIssueOut;
  ai: AnalysisJobHandle;
  matchingRunbooks: { id: string; title: string; trigger_signature: string | null; tier: string | null }[];
}) {
  const navigate = useNavigate();
  const analysis = ai.job;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {issue.site ? (
        <SiteHealthSection site={issue.site} />
      ) : (
        <section>
          <h4 style={{ marginBottom: 6, marginTop: 0 }}>Fleet-wide issue</h4>
          <p style={{ color: 'var(--nx-muted)', margin: 0 }}>
            This issue is not attributed to any one site, so there is nothing site-scoped to show
            here. Trace it from a specific site's own issue record.
          </p>
        </section>
      )}

      <section>
        <h4 style={{ marginBottom: 6, marginTop: 0 }}>AI recovery triage</h4>
        {/* Unchanged label and behaviour: this is still "start an
            analysis for this issue", and a cache hit simply paints the
            answer instead of spinning. Re-analyze -- the control that
            deliberately bypasses that cache -- lives next to the RESULT
            it replaces, not here. */}
        <button type="button" onClick={() => ai.start()} disabled={ai.starting}>
          {ai.starting ? 'Starting…' : 'Analyze with AI'}
        </button>
        {ai.startError != null && (
          <div style={{ color: 'var(--nx-error)' }}>{String(ai.startError)}</div>
        )}
        {/* The POLL's failure, which this pane used to drop while
            TicketsPane showed it. A failed poll is terminal for the
            job: nothing will fetch it again, so the status line below
            is frozen at whatever the POST said and only this line says
            why. Without it the pane reads as a run that is still
            going, next to a Re-analyze button that is the only way
            out. */}
        {ai.pollError != null && (
          <div style={{ color: 'var(--nx-error)' }} data-testid="analysis-poll-error">
            {String(ai.pollError)}
          </div>
        )}
        {analysis && (
          <AnalysisJobView
            job={analysis}
            runHref={(id) => actHref(issue, id)}
            onReanalyze={() => ai.start({ force: true })}
            reanalyzeDisabled={ai.busy}
          />
        )}
      </section>

      <section>
        <h4 style={{ marginBottom: 6, marginTop: 0 }}>Suggested runbooks</h4>
        {matchingRunbooks.length === 0 ? (
          <p style={{ color: 'var(--nx-muted)' }}>
            {issue.derived_signature
              ? `No runbook advertises ${issue.derived_signature}, as a trigger or a secondary one.`
              : 'This issue has no derived signature, so nothing can be matched against the catalogue.'}
          </p>
        ) : (
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {matchingRunbooks.map((r) => (
              <li key={r.id} style={{ marginBottom: 6 }}>
                <span style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <span>{r.title}</span>
                  <span style={{ color: 'var(--nx-muted)', fontSize: 12 }}>
                    ({r.trigger_signature}, {r.tier})
                  </span>
                  <RunLink
                    to={actHref(issue, r.id)}
                    testId={`run-runbook-${r.id}`}
                    title={`Open ${r.id} in Act, pre-filled for this issue`}
                  />
                </span>
              </li>
            ))}
          </ul>
        )}
        {/* The no-runbook fallback, unchanged: lands on the whole
            catalogue with site and reason pre-filled but nothing
            pinned. */}
        <button
          type="button"
          style={{ marginTop: 8 }}
          onClick={() => navigate(actHref(issue))}
        >
          Act on this issue →
        </button>
        {matchingRunbooks.length > 0 && matchingRunbooks[0]?.trigger_signature && (
          <p style={{ marginTop: 6 }}>
            <button
              type="button"
              style={{ fontSize: 12 }}
              onClick={() => navigate(`/knowledge?signature=${matchingRunbooks[0]!.trigger_signature}`)}
            >
              View AI knowledge for {matchingRunbooks[0]!.trigger_signature} →
            </button>
          </p>
        )}
      </section>

      <p style={{ fontSize: 11, color: 'var(--nx-muted)', margin: 0 }}>
        Site operators see this issue's triage state and its timestamp in their own Support
        screen, grouped into Open / In progress / Closed. Marking "in progress" is how they know
        someone has picked it up. Which runbook you ran, and who ran it, stays in the support Audit
        tab.
      </p>
    </div>
  );
}

interface LogRunner {
  query: string;
  setQuery: (q: string) => void;
  run: () => void;
  isError: boolean;
  error: unknown;
  data: SupportLogsResponse | undefined;
}

/** Evidence: the two raw sources under this issue — what the site
 *  logged, and the scrubbed payload the ingester recorded (which is
 *  precisely what the AI analysis is handed). */
function EvidenceTab({ issue, logs }: { issue: SupportIssueOut; logs: LogRunner }) {
  const cfg = getRuntimeConfig();
  const presets = useMemo(() => logPresetsForIssue(issue), [issue]);

  // `Issue.site_id` is nullable, so a cross-site issue has no tenant
  // to scope a log query to -- Loki would be handed an empty
  // X-Scope-OrgID and the api-server rejects it with a confusing 400.
  // Disable the whole log section instead of letting the operator
  // fire a request that cannot succeed (found in review).
  const canQueryLogs = Boolean(issue.site);

  const grafanaUrl =
    cfg.grafanaBaseUrl && issue.site
      ? grafanaExploreUrl(cfg.grafanaBaseUrl, issue.site, logs.query, issue.last_seen_ms)
      : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <section>
        <h4 style={{ marginBottom: 6, marginTop: 0 }}>Logs</h4>
        {!canQueryLogs ? (
          <p style={{ color: 'var(--nx-muted)' }}>
            This issue has no site, so there is no Loki tenant to query. Cross-site issues must be
            traced from a specific site's own issue record.
          </p>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
              {presets.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => logs.setQuery(p.logql)}
                  style={{
                    fontSize: 12,
                    padding: '4px 10px',
                    borderRadius: 'var(--nx-r)',
                    border: '1px solid var(--nx-border)',
                    background: p.logql === logs.query ? 'var(--nx-bg3)' : 'transparent',
                    color: 'var(--nx-text)',
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                value={logs.query}
                onChange={(e) => logs.setQuery(e.target.value)}
                style={{ flex: 1, padding: 6, fontFamily: 'monospace', fontSize: 12 }}
                aria-label="LogQL query"
              />
              <button type="button" onClick={logs.run} disabled={!logs.query.trim()}>
                Run query
              </button>
              {grafanaUrl && (
                <a
                  href={grafanaUrl}
                  target="_blank"
                  rel="noreferrer"
                  style={{ alignSelf: 'center', fontSize: 13 }}
                  // Plan: "The console owns triage and action; Grafana
                  // owns exploration" -- the bounded query above is
                  // capped server-side (500 lines / 24h), so anything
                  // open-ended belongs in Explore, not here.
                  title="Open this query in Grafana Explore for unbounded exploration"
                >
                  Open in Grafana ↗
                </a>
              )}
            </div>
            {logs.isError && (
              <div style={{ color: 'var(--nx-error)', marginTop: 8 }}>
                Log query failed: {String(logs.error)}
              </div>
            )}
            {logs.data && (
              <pre
                style={{
                  marginTop: 10,
                  maxHeight: 240,
                  overflow: 'auto',
                  background: 'var(--nx-bg3)',
                  padding: 10,
                  fontSize: 12,
                }}
              >
                {logs.data.lines.length === 0
                  ? '(no matching lines)'
                  : logs.data.lines
                      .map((l) => `${new Date(l.at_ms).toISOString()}  ${l.text}`)
                      .join('\n')}
              </pre>
            )}
          </>
        )}
      </section>

      <section>
        <h4 style={{ marginBottom: 6, marginTop: 0 }}>Ingested detail</h4>
        {issue.detail != null ? (
          // The scrubbed ingestion payload — the richest evidence the
          // platform holds for this issue and exactly what the AI
          // analysis is handed. Collapsed by default: it is raw JSON.
          <details>
            <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--nx-muted)' }}>
              Raw detail
            </summary>
            <pre
              style={{
                marginTop: 6,
                maxHeight: 240,
                overflow: 'auto',
                background: 'var(--nx-bg3)',
                padding: 10,
                fontSize: 12,
                whiteSpace: 'pre-wrap',
              }}
            >
              {JSON.stringify(issue.detail, null, 2)}
            </pre>
          </details>
        ) : (
          <p style={{ color: 'var(--nx-muted)', margin: 0 }}>
            The ingester recorded no payload for this issue — its title and source are all the
            evidence the platform holds.
          </p>
        )}
      </section>
    </div>
  );
}

/** Knowledge: exactly the block the assistant is handed for this
 *  issue's derived signature, and the route into the full pane. */
function KnowledgeTab({ issue }: { issue: SupportIssueOut }) {
  const navigate = useNavigate();
  return (
    <section>
      <h4 style={{ marginBottom: 6, marginTop: 0 }}>Knowledge</h4>
      {issue.derived_signature ? (
        <>
          {/* The backend derived this signature with the SAME logic
              the analysis job uses, so this preview is precisely the
              knowledge block the model would be handed. */}
          <KnowledgePreview signature={issue.derived_signature} site={issue.site ?? ''} />
          <p style={{ marginTop: 6 }}>
            <button
              type="button"
              style={{ fontSize: 12 }}
              onClick={() =>
                navigate(`/knowledge?signature=${encodeURIComponent(issue.derived_signature!)}`)
              }
            >
              View in Knowledge →
            </button>
          </p>
        </>
      ) : (
        <p style={{ color: 'var(--nx-muted)' }}>
          No failure signature could be derived from this issue's title/source/detail — the AI
          will run from live tools only, with no doctrine or lessons.
        </p>
      )}
    </section>
  );
}

/** History: has this been seen elsewhere, and what has already been
 *  tried at this site. The second half answers "did someone already
 *  run the thing I am about to run", which previously required
 *  leaving for the Audit pane and reading it by hand. */
function HistoryTab({ issue }: { issue: SupportIssueOut }) {
  // Cross-site signature correlation (plan, Pane 2: "this signature
  // also fired at N other sites in the last 24h") -- re-queries
  // /support/issues with NO site filter, same fingerprint, so it
  // naturally spans every site the caller can see.
  const correlationQuery = useQuery({
    queryKey: ['support', 'issues', 'signature', issue.fingerprint],
    queryFn: () => listSupportIssues({ signature: issue.fingerprint, limit: 50 }),
  });

  // Durable runbook runs for this site -- the same endpoint ActPane's
  // awaiting-outcome queue already calls, filtered by site.
  const runsQuery = useQuery({
    queryKey: ['support', 'runbook-runs', 'site', issue.site],
    queryFn: () => listRunbookRuns({ site: issue.site as string }),
    enabled: Boolean(issue.site),
  });

  const otherSites = new Set(
    (correlationQuery.data?.issues ?? []).filter((i) => i.id !== issue.id).map((i) => i.site),
  );
  const runs = runsQuery.data ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <section>
        <h4 style={{ marginBottom: 6, marginTop: 0 }}>Cross-site correlation</h4>
        {correlationQuery.isLoading ? (
          <SkeletonLoader shape="text-line" count={2} />
        ) : correlationQuery.isError ? (
          <p style={{ color: 'var(--nx-error)' }}>Correlation unavailable (query failed).</p>
        ) : otherSites.size > 0 ? (
          <p>
            This signature also fired at <strong>{otherSites.size}</strong> other site
            {otherSites.size === 1 ? '' : 's'}: {[...otherSites].join(', ')}
          </p>
        ) : (
          <p style={{ color: 'var(--nx-muted)' }}>Not seen at any other site.</p>
        )}
      </section>

      <section>
        <h4 style={{ marginBottom: 6, marginTop: 0 }}>Recent runbook runs at this site</h4>
        {!issue.site ? (
          <p style={{ color: 'var(--nx-muted)', margin: 0 }}>
            No site on this issue, so there is no per-site run history to show.
          </p>
        ) : runsQuery.isLoading ? (
          <SkeletonLoader shape="text-line" count={2} />
        ) : runsQuery.isError ? (
          <p style={{ color: 'var(--nx-error)', margin: 0 }}>
            Run history unavailable ({describeQueryError(runsQuery.error)}).
          </p>
        ) : runs.length === 0 ? (
          <p style={{ color: 'var(--nx-muted)', margin: 0 }}>
            No runbook has been run at this site yet.
          </p>
        ) : (
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
            {runs.slice(0, 20).map((r) => (
              <li key={r.run_id} style={{ marginBottom: 4 }}>
                <strong>{r.runbook_id}</strong> · {new Date(r.created_at_ms).toLocaleString()}{' '}
                <span style={{ color: 'var(--nx-muted)' }}>
                  ({r.status}
                  {r.requested_by_email ? `, by ${r.requested_by_email}` : ''}
                  {r.resolved === null
                    ? ', outcome not recorded'
                    : r.resolved
                      ? ', fixed it'
                      : ', did not fix it'}
                  )
                </span>
              </li>
            ))}
          </ul>
        )}
        {issue.site && (
          // The two cross-pane handoffs. Real links (not buttons) so
          // they can be opened in a new tab and pasted to a colleague,
          // which is most of the point of a `?site=` URL.
          <p style={{ display: 'flex', gap: 12, marginTop: 10, marginBottom: 0, fontSize: 13 }}>
            <Link
              to={siteHandoffHref('audit', issue.site)}
              data-testid="history-audit-for-site"
              title={`Open the audit trail filtered to ${issue.site}`}
            >
              Audit for this site →
            </Link>
            <Link
              to={siteHandoffHref('tickets', issue.site)}
              data-testid="history-tickets-for-site"
              title={`Open user-reported tickets filtered to ${issue.site}`}
            >
              Tickets for this site →
            </Link>
          </p>
        )}
      </section>
    </div>
  );
}

const DETAIL_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'evidence', label: 'Evidence' },
  { id: 'knowledge', label: 'Knowledge' },
  { id: 'history', label: 'History' },
] as const;

type DetailTabId = (typeof DETAIL_TABS)[number]['id'];

/**
 * One issue, worked from a pinned header.
 *
 * The header (title, site, severity, fingerprint, triage controls and
 * Run) never scrolls away, because everything an engineer does to an
 * incident is one of those controls and they used to be metres above
 * whatever they were reading. Everything else is a tab, so the panel
 * has one job at a time instead of eight stacked sections.
 *
 * State that must survive a tab switch (the LogQL box and its results,
 * a running analysis) lives HERE rather than in the panel components,
 * which unmount when you leave them -- a poll started on Overview keeps
 * polling while you read the logs.
 *
 * The started analysis JOB is the exception: it lives in the pane,
 * keyed by issue id, and is passed in. This component is keyed by
 * issue id and so remounts whenever the selection changes -- if the job
 * lived here, an analysis that is still running server-side would lose
 * its poll the moment the engineer looked at another issue (or the
 * moment a "Load older issues" briefly took the row out from under
 * them), with no route back to the result. Held one level up, a
 * remount simply re-attaches to the job that is already running.
 *
 * What is held up there is the whole POST RESPONSE, not just its id:
 * `cached: true` is reported only by the POST (the GET cannot know it),
 * so an id alone would lose the one fact that says how much to trust
 * the answer the next time the engineer opens this issue.
 */
function IssueDetail({
  issue,
  startedAnalysis,
  onAnalysisStarted,
}: {
  issue: SupportIssueOut;
  startedAnalysis: SupportAnalysisResponse | null;
  onAnalysisStarted: (issueId: string, resp: SupportAnalysisResponse) => void;
}) {
  const [tab, setTab] = useState<DetailTabId>('overview');
  const presets = useMemo(() => logPresetsForIssue(issue), [issue]);
  const [logQuery, setLogQuery] = useState(presets[0]?.logql ?? '');
  const tabRefs = useRef<Partial<Record<DetailTabId, HTMLButtonElement | null>>>({});

  // Runbooks whose trigger_signature matches THIS issue -- the plan's
  // "the UI lists runbooks whose trigger signature matches the
  // detected failure". `Issue.fingerprint` is a fine-grained
  // per-occurrence hash while `trigger_signature` is classify.py's
  // coarse `kind` enum, so they are NOT the same vocabulary and can
  // never be compared directly (documented at length in Step 6's
  // tracker entry). The bridge is the issue TITLE, which the
  // classifier writes the kind into. Matched EXACTLY against the
  // server's own derived signature (see below), and still shown as
  // "possible matches" rather than presented as authoritative.
  const runbooksQuery = useQuery({
    queryKey: ['support', 'runbooks'],
    queryFn: () => listSupportRunbooks(),
  });
  // THE SERVER'S SIGNATURE, NOT A SECOND HEURISTIC. `derived_signature`
  // is `_derive_trigger_signature`'s verdict for this issue, and it is
  // the same one the AI panel beside this list is keyed on.
  //
  // This used to be a plain substring scan over the issue title, and
  // SIGNATURE NAMES NEST: an issue titled `door_adapter_down: L4_SLL_Door`
  // contains `adapter_down`, so a DOOR controller being offline offered
  // a `gausium-phantas-fleet-adapter` restart -- which
  // `orphaned-task-retire` calls the thing that CREATES uncancellable
  // orphan tasks -- and `recover-zenoh-routing`, an ordered restart of
  // five services including the site worker. Each with a `Run ->` link
  // that opens a pre-filled confirm form. The backend hit exactly this
  // on 2026-09-02 and fixed it by longest-match; its comment is
  // "wrong doctrine is worse than no doctrine", and this copy was never
  // fixed. Reading the server's answer is better than repeating the fix
  // in a second place.
  //
  // AND `also_triggered_by` COUNTS. No runbook declares `no_bids` or
  // `robot_dropped` as its PRIMARY trigger; `zenoh-routing-recovery`
  // declares both as secondary. Matching on `trigger_signature` alone
  // printed "No runbook advertises a trigger signature matching this
  // issue" for the site's two commonest transport-wedge signatures,
  // while the AI panel on the same screen named the runbook.
  const derived = (issue.derived_signature || '').toLowerCase();
  const matchingRunbooks = derived
    ? (runbooksQuery.data?.runbooks ?? []).filter((r) => {
        // A runbook the catalogue marks unfinished is not a suggestion.
        // Every entry in the shipped catalogue is `available: true`
        // today, so this filters nothing at present -- it is here for
        // the next entry that is authored before it is finished.
        if (!r.available) return false;
        const signatures = [r.trigger_signature, ...(r.also_triggered_by ?? [])];
        return signatures.some((s) => (s || '').toLowerCase() === derived);
      })
    : [];

  const logsQuery = useQuery({
    queryKey: ['support', 'logs', issue.site, logQuery],
    queryFn: () => getSupportLogs({ site: issue.site as string, query: logQuery }),
    enabled: false,
  });

  // Start + poll + cadence + the cache-hit rule, all from the one
  // implementation shared with TicketsPane.
  const ai = useAnalysisJob({
    target: issue.id,
    started: startedAnalysis,
    onStarted: useCallback(
      (resp: SupportAnalysisResponse) => onAnalysisStarted(issue.id, resp),
      [issue.id, onAnalysisStarted],
    ),
  });

  /**
   * The APG tabs keyboard contract, which the `role="tab"` markup had
   * been promising without implementing: exactly one tab is in the tab
   * sequence (the selected one -- a roving tabindex), and once focus is
   * inside the tablist the arrow keys move BOTH focus and selection.
   * Home/End jump to the ends. Without this a keyboard user landed on
   * the first tab and could reach no other, because every other tab was
   * announced as a tab and therefore skipped by Tab.
   */
  const onTabKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    const current = DETAIL_TABS.findIndex((t) => t.id === tab);
    const last = DETAIL_TABS.length - 1;
    let nextIndex: number;
    switch (event.key) {
      case 'ArrowRight':
        nextIndex = current === last ? 0 : current + 1;
        break;
      case 'ArrowLeft':
        nextIndex = current === 0 ? last : current - 1;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = last;
        break;
      default:
        return;
    }
    // Only now, once we know the key is ours: ArrowLeft/Right inside a
    // tablist must not also scroll the panel, but every other key
    // (Tab, typing into nothing, browser shortcuts) must still behave.
    event.preventDefault();
    const nextId = DETAIL_TABS[nextIndex]!.id;
    setTab(nextId);
    tabRefs.current[nextId]?.focus();
  };

  return (
    <>
      <div className="nx-diagnose__detail-head" data-testid="diagnose-issue-header">
        <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, flex: '1 1 auto' }}>{issue.title}</h3>
          <StatusChip status={severityToStatus(issue.severity)} label={issue.severity} />
          <StatusChip status={triageChipKind(issue.triage_state)} label={TRIAGE_LABEL[issue.triage_state]} />
          <RunLink
            to={actHref(issue)}
            testId="run-issue"
            title="Open the runbook catalogue in Act, pre-filled for this issue"
          />
        </div>
        <div
          style={{
            color: 'var(--nx-muted)',
            fontSize: 12,
            marginTop: 4,
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <span>
            {issue.site ?? 'cross-site'} · {issue.source} · seen {issue.count}× · last{' '}
            {new Date(issue.last_seen_ms).toLocaleString()}
          </span>
          <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            ref <IssueReference value={issue.reference} />
          </span>
          <span>
            fingerprint <code>{issue.fingerprint}</code>
          </span>
        </div>
        <div style={{ marginTop: 8 }}>
          <TriageControl issue={issue} />
        </div>
        <div
          className="nx-diagnose__tabs"
          role="tablist"
          aria-label="Issue detail sections"
        >
          {DETAIL_TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              id={`diagnose-tab-${t.id}`}
              ref={(el) => {
                tabRefs.current[t.id] = el;
              }}
              aria-selected={tab === t.id}
              aria-controls="diagnose-detail-panel"
              // Roving tabindex: the tablist is ONE stop in the tab
              // sequence and the arrows move within it.
              tabIndex={tab === t.id ? 0 : -1}
              data-testid={`diagnose-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              onKeyDown={onTabKeyDown}
              style={{
                padding: '4px 12px',
                borderRadius: 999,
                border: '1px solid var(--nx-border)',
                background: tab === t.id ? 'var(--nx-bg3)' : 'transparent',
                color: tab === t.id ? 'var(--nx-white)' : 'var(--nx-muted)',
                cursor: 'pointer',
                fontSize: 12,
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div
        className="nx-diagnose__tabpanel"
        role="tabpanel"
        id="diagnose-detail-panel"
        aria-labelledby={`diagnose-tab-${tab}`}
        data-testid={`diagnose-panel-${tab}`}
      >
        {tab === 'overview' && (
          <OverviewTab issue={issue} matchingRunbooks={matchingRunbooks} ai={ai} />
        )}
        {tab === 'evidence' && (
          <EvidenceTab
            issue={issue}
            logs={{
              query: logQuery,
              setQuery: setLogQuery,
              run: () => void logsQuery.refetch(),
              isError: logsQuery.isError,
              error: logsQuery.error,
              data: logsQuery.data,
            }}
          />
        )}
        {tab === 'knowledge' && <KnowledgeTab issue={issue} />}
        {tab === 'history' && <HistoryTab issue={issue} />}
      </div>
    </>
  );
}

const STATUS_FILTER_OPTIONS: TriageState[] = ['open', 'in_progress', 'resolved'];

export function DiagnosePane() {
  const qc = useQueryClient();
  const [titleSearch, setTitleSearch] = useState('');
  const [statusColumnFilter, setStatusColumnFilter] = useState<TriageState | ''>('');
  // Bulk triage (go-live backlog clearing): row checkboxes feed one
  // "Resolve selected" action. A Set of issue ids, not row objects, so
  // a refetch replacing the row instances cannot orphan a selection.
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [bulkNote, setBulkNote] = useState<string | null>(null);
  // Cursors already pulled in by "Load older issues", each of which is
  // its own cached query below.
  const [pageCursors, setPageCursors] = useState<string[]>([]);
  // Analysis jobs started in this session, keyed by issue id. Lifted
  // out of `IssueDetail` (which is keyed by issue id and so remounts
  // whenever the selection moves) so a job that is still running
  // server-side can be re-attached to instead of orphaned -- see that
  // component's docstring. The whole POST response is kept, not the id:
  // only the POST reports `cached`.
  const [analysisJobs, setAnalysisJobs] = useState<ReadonlyMap<string, SupportAnalysisResponse>>(
    new Map(),
  );
  const rememberAnalysisJob = useCallback((issueId: string, resp: SupportAnalysisResponse) => {
    setAnalysisJobs((prev) => new Map(prev).set(issueId, resp));
  }, []);

  // `?issue=` and `?site=` are the SOURCE of both selections, not a
  // mirror of component state: an issue is then deep-linkable and
  // shareable ("look at NX-0042"), and the site filter survives a trip
  // to Act and back. Selection is an ID here -- the issue object is
  // derived from live query data below, so a triage action repaints
  // the panel instead of leaving a stale snapshot on screen.
  const [params, setParams] = useSearchParams();
  const selectedId = params.get('issue');
  const siteColumnFilter = params.get('site') ?? '';

  const setParam = useCallback(
    (key: 'issue' | 'site', value: string | null) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set(key, value);
          else next.delete(key);
          return next;
        },
        // A filter change and a row click are not navigations worth a
        // history entry each -- the URL still always reflects the
        // current view, which is what makes it shareable.
        { replace: true },
      );
    },
    [setParams],
  );

  // Ids currently on screen. Read by the head page's reconciliation
  // walk, which runs inside a query function and therefore must not
  // depend on a render-time closure: a ref is what lets the walk see
  // what the table is actually showing at the moment it runs.
  const loadedIdsRef = useRef<ReadonlySet<string>>(new Set());

  /**
   * The issue list, as one cached query PER PAGE rather than one query
   * that re-derives the whole window from a frozen list of cursors.
   *
   * The old shape had two faults that only showed up in motion. Issues
   * come back newest-first, so every arriving row shifts the window
   * down; re-fetching page 0 and then re-using the ORIGINAL cursor for
   * page 1 left the rows that had slid into the gap between them
   * fetched by no request at all -- they simply vanished from the table
   * and nothing said the list was incomplete. And because the query key
   * carried the cursor array, "Load older issues" changed the key,
   * dropped to `pending`, and blanked the pane (taking the open issue
   * and any running analysis with it).
   *
   * One query per page fixes both. An older page is keyed by its own
   * cursor, so it is fetched once, cached, and never blanks or
   * re-fetches when another page is added -- which is also why the 15s
   * poll costs exactly ONE request no matter how deep the operator has
   * paged (`useInfiniteQuery` was the other candidate here and is
   * rejected for precisely that: its refetch re-reads every loaded page
   * in sequence).
   *
   * Only the head page is polled, and it closes its own window: if the
   * newest page comes back sharing no row with what is already loaded,
   * then more than a page of issues arrived inside one tick and there
   * is a band in between that nobody has fetched, so it keeps walking
   * next_cursor until it meets a row it already holds. Overlapping by
   * even one row proves the two ranges are contiguous, which is the
   * whole guarantee: no row the server has inside the loaded window can
   * be missing from the table after a refresh.
   */
  const issuesQuery = useQueries({
    queries: [
      {
        queryKey: ['support', 'issues', 'page', HEAD_PAGE_KEY],
        queryFn: async (): Promise<SupportIssuesResponse> => {
          const issues: SupportIssueOut[] = [];
          let cursor: string | undefined;
          let nextCursor: string | null = null;
          for (let walked = 0; walked < MAX_RECONCILE_PAGES; walked += 1) {
            const page: SupportIssuesResponse = await listSupportIssues({
              limit: PAGE_LIMIT,
              cursor,
            });
            issues.push(...page.issues);
            nextCursor = page.next_cursor;
            const known = loadedIdsRef.current;
            // Nothing loaded yet: this page IS the window.
            if (known.size === 0) break;
            // Met a row we already hold -- the ranges touch, so the
            // union of them has no hole.
            if (page.issues.some((row) => known.has(row.id))) break;
            if (!page.next_cursor) break;
            cursor = page.next_cursor;
          }
          return { issues, next_cursor: nextCursor };
        },
        // The console is left open during an incident; a list that only
        // updates on a manual reload is how a resolved issue stays on
        // screen and an arriving one goes unnoticed.
        refetchInterval: REFRESH_MS,
      },
      ...pageCursors.map((cursor) => ({
        queryKey: ['support', 'issues', 'page', cursor],
        queryFn: (): Promise<SupportIssuesResponse> =>
          listSupportIssues({ limit: PAGE_LIMIT, cursor }),
      })),
    ],
    // Memoised by react-query against the underlying results, so the
    // merged array is referentially stable between renders and the
    // derived memos below do not churn.
    combine: (results) => {
      // De-duplicated by id: a keyset cursor is stable for older rows,
      // but an issue whose `last_seen` moves between two page fetches
      // can legitimately appear on two of them. The newest page wins,
      // being the freshest copy of that row.
      const seen = new Set<string>();
      const issues: SupportIssueOut[] = [];
      for (const result of results) {
        for (const row of result.data?.issues ?? []) {
          if (seen.has(row.id)) continue;
          seen.add(row.id);
          issues.push(row);
        }
      }
      // Re-sorted rather than trusted to arrive ordered: each page is
      // ordered in itself, but a row that moved between fetches would
      // otherwise sit wherever the page that found it happened to put
      // it.
      issues.sort((a, b) => b.last_seen_ms - a.last_seen_ms || a.id.localeCompare(b.id));
      const head = results[0];
      const last = results[results.length - 1];
      const failed = results.find((r) => r.isError);
      return {
        issues,
        // The cursor to continue from is the deepest page's, not the
        // head's -- that is where the loaded window ends.
        nextCursor: last?.data?.next_cursor ?? null,
        // Only the head page gates the skeleton: an older page still
        // in flight must never blank rows that are already on screen.
        isLoading: head?.isPending ?? true,
        isFetching: results.some((r) => r.isFetching),
        isError: failed !== undefined,
        error: failed?.error ?? null,
        dataUpdatedAt: results.reduce((acc, r) => Math.max(acc, r.dataUpdatedAt), 0),
      };
    },
  });

  const allIssues = issuesQuery.issues;
  const nextCursor = issuesQuery.nextCursor;

  useEffect(() => {
    loadedIdsRef.current = new Set(allIssues.map((row) => row.id));
  }, [allIssues]);

  // The complete site vocabulary, not "sites that happen to have an
  // issue on the loaded page" -- otherwise the filter silently cannot
  // express "show me the site that just went quiet".
  const sitesQuery = useQuery({
    queryKey: ['support', 'sites'],
    queryFn: listSupportSites,
    staleTime: 60_000,
  });

  const siteOptions = useMemo(() => {
    const labels = new Map<string, string>();
    for (const s of sitesQuery.data ?? []) labels.set(s.site, s.label || s.site);
    // A site with issues but no registry row would otherwise be
    // unfilterable; add it rather than pretend it does not exist.
    for (const issue of allIssues) {
      if (issue.site && !labels.has(issue.site)) labels.set(issue.site, issue.site);
    }
    return [...labels.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [sitesQuery.data, allIssues]);

  const filteredIssues = useMemo(() => {
    const needle = titleSearch.trim().toLowerCase();
    return allIssues.filter((row) => {
      if (needle && !row.title.toLowerCase().includes(needle)) return false;
      if (siteColumnFilter && row.site !== siteColumnFilter) return false;
      if (statusColumnFilter && row.triage_state !== statusColumnFilter) return false;
      return true;
    });
  }, [allIssues, titleSearch, siteColumnFilter, statusColumnFilter]);

  // THE stale-selection fix: the panel reads whichever row currently
  // carries the selected id, so a triage write (or the 15s refresh)
  // repaints it. Derived from the unfiltered list on purpose -- a
  // deep link to an issue the current filter hides still opens it.
  const live = useMemo(
    () => (selectedId ? (allIssues.find((row) => row.id === selectedId) ?? null) : null),
    [allIssues, selectedId],
  );

  // ...and the counterweight to it: a row can LEAVE the loaded window
  // mid-triage (a colleague resolves it, or newer arrivals push it past
  // the last loaded page), and deriving the panel from live data alone
  // meant it blanked to "Issue not loaded" under the engineer's hands.
  // The last row that did resolve for this id is kept and shown with a
  // note saying it is no longer live, so the work in progress survives;
  // "Issue not loaded" is now reserved for an id that never resolved at
  // all (a bad or very old deep link).
  const lastResolvedRef = useRef<{ id: string; row: SupportIssueOut } | null>(null);
  useEffect(() => {
    if (selectedId && live) lastResolvedRef.current = { id: selectedId, row: live };
  }, [selectedId, live]);
  const remembered =
    selectedId && lastResolvedRef.current?.id === selectedId ? lastResolvedRef.current.row : null;
  const selected = live ?? remembered;
  const selectionIsStale = live === null && selected !== null;

  const allFilteredSelected =
    filteredIssues.length > 0 && filteredIssues.every((row) => checkedIds.has(row.id));

  const toggleOne = (id: string) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Select-all operates on the FILTERED view (what the header checkbox
  // visually governs), adding/removing only those ids so a selection
  // made under another filter survives.
  const toggleSelectAll = () => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        for (const row of filteredIssues) next.delete(row.id);
      } else {
        for (const row of filteredIssues) next.add(row.id);
      }
      return next;
    });
  };

  const bulkResolveMutation = useMutation({
    mutationFn: () => bulkSetIssueState([...checkedIds], 'resolved'),
    onSuccess: (resp) => {
      setBulkNote(
        `Resolved ${resp.updated} issue${resp.updated === 1 ? '' : 's'}` +
          (resp.missing.length > 0 ? ` · ${resp.missing.length} not found` : ''),
      );
      setCheckedIds(new Set());
      void qc.invalidateQueries({ queryKey: ['support', 'issues'] });
    },
  });

  const columns: DataTableColumn<SupportIssueOut>[] = [
    {
      id: 'select',
      width: '32px',
      header: (
        // stopPropagation on both controls: a checkbox click must
        // never double as a row click (same rule HeaderFilter
        // documents for its in-header controls).
        <input
          type="checkbox"
          aria-label="Select all issues"
          checked={allFilteredSelected}
          onChange={toggleSelectAll}
          onClick={(e) => e.stopPropagation()}
        />
      ),
      cell: (row) => (
        <input
          type="checkbox"
          aria-label={`Select issue ${row.reference}`}
          checked={checkedIds.has(row.id)}
          onChange={() => toggleOne(row.id)}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },
    { id: 'reference', header: 'Reference', cell: (row) => <IssueReference value={row.reference} /> },
    { id: 'title', header: 'Title', cell: (row) => row.title, sortable: true, sortValue: (row) => row.title },
    {
      id: 'site',
      header: (
        <HeaderFilterSelect
          label="Site"
          value={siteColumnFilter}
          options={siteOptions}
          allLabel="All sites"
          onChange={(v) => setParam('site', v || null)}
        />
      ),
      cell: (row) => row.site ?? '—',
    },
    {
      id: 'status',
      header: (
        <HeaderFilterSelect
          label="Status"
          value={statusColumnFilter}
          options={STATUS_FILTER_OPTIONS.map((s) => ({ value: s, label: TRIAGE_LABEL[s] }))}
          allLabel="All statuses"
          onChange={(v) => setStatusColumnFilter(v as TriageState | '')}
        />
      ),
      cell: (row) => (
        <StatusChip status={triageChipKind(row.triage_state)} label={TRIAGE_LABEL[row.triage_state]} />
      ),
    },
    {
      id: 'severity',
      header: 'Severity',
      cell: (row) => <StatusChip status={severityToStatus(row.severity)} label={row.severity} />,
    },
    { id: 'count', header: 'Count', cell: (row) => row.count, align: 'right' },
    {
      id: 'last_seen',
      header: 'Last seen',
      cell: (row) => new Date(row.last_seen_ms).toLocaleString(),
      sortable: true,
      sortValue: (row) => row.last_seen_ms,
    },
  ];

  return (
    <div className="nx-diagnose">
      <div className="nx-diagnose__head">
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
          <h2 style={{ marginTop: 0, marginBottom: 8 }}>Diagnose</h2>
          {/* Same reason InventoryPane states its sample time: a list
              that quietly stopped refreshing looks exactly like a
              fleet with nothing new to report.

              Deliberately NOT a live region: its text carries the
              refresh timestamp, so an `aria-live` here re-announced the
              whole line to a screen reader every 15 seconds, forever,
              for a change that is never news. */}
          <span
            style={{ fontSize: 12, color: 'var(--nx-muted)' }}
            data-testid="diagnose-refresh-line"
          >
            {issuesQuery.isFetching
              ? 'Refreshing…'
              : issuesQuery.isError
                ? 'Refresh failed — retrying'
                : issuesQuery.dataUpdatedAt
                  ? `Refreshed ${new Date(issuesQuery.dataUpdatedAt).toLocaleTimeString()} · every 15s`
                  : ''}
          </span>
          {/* The announcement, on its own node, carrying only the one
              transition worth interrupting for. It renders empty the
              rest of the time, so nothing is spoken on a healthy tick. */}
          <span
            className="nx-sr-only"
            role="status"
            aria-live="polite"
            data-testid="diagnose-refresh-status"
          >
            {issuesQuery.isError ? 'Refresh failed — retrying' : ''}
          </span>
        </div>
        <input
          placeholder="Search issue titles…"
          value={titleSearch}
          onChange={(e) => setTitleSearch(e.target.value)}
          aria-label="Search issue titles"
          style={{ marginBottom: 10, padding: 6, width: '100%' }}
        />
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
          <button
            type="button"
            disabled={checkedIds.size === 0 || bulkResolveMutation.isPending}
            onClick={() => {
              setBulkNote(null);
              bulkResolveMutation.mutate();
            }}
          >
            {bulkResolveMutation.isPending
              ? 'Resolving…'
              : `Resolve selected (${checkedIds.size})`}
          </button>
          {bulkNote && (
            <span style={{ fontSize: 12, color: 'var(--nx-muted)' }}>{bulkNote}</span>
          )}
          {bulkResolveMutation.isError && (
            <span style={{ fontSize: 12, color: 'var(--nx-error)' }}>
              {String(bulkResolveMutation.error)}
            </span>
          )}
          {siteColumnFilter && (
            <button type="button" style={{ fontSize: 12 }} onClick={() => setParam('site', null)}>
              Clear site filter ({siteColumnFilter})
            </button>
          )}
        </div>
      </div>
      <div className="nx-diagnose__layout">
        <div className="nx-diagnose__list-col">
          {issuesQuery.isLoading ? (
            <SkeletonLoader shape="table-row" count={6} />
          ) : issuesQuery.isError && allIssues.length === 0 ? (
            // Only when there is genuinely nothing to show. One flaky
            // 15s poll used to replace a populated table with this
            // error page; the rows are still good, and the refresh line
            // above already says "Refresh failed — retrying".
            <EmptyState title="Could not load issues" description={String(issuesQuery.error)} />
          ) : (
            <>
              <DataTable
                ariaLabel="Issues"
                columns={columns}
                rows={filteredIssues}
                getRowId={(row) => row.id}
                onRowClick={(row) => setParam('issue', row.id)}
                emptyState={<EmptyState title="No issues" description="No issues match this filter." />}
              />
              {/* The list used to stop dead at 100 rows with nothing
                  saying so -- an issue older than the newest hundred
                  was simply unreachable from this pane. */}
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                  marginTop: 8,
                  fontSize: 12,
                  color: 'var(--nx-muted)',
                }}
              >
                <span>
                  Showing {filteredIssues.length}
                  {filteredIssues.length !== allIssues.length ? ` of ${allIssues.length}` : ''} issue
                  {allIssues.length === 1 ? '' : 's'}
                </span>
                {nextCursor ? (
                  <button
                    type="button"
                    disabled={issuesQuery.isFetching}
                    // Guarded against a double click landing the same
                    // cursor twice: each cursor is one cached query,
                    // and two entries for one key is a page counted
                    // twice in the "Showing N issues" line.
                    onClick={() =>
                      setPageCursors((prev) =>
                        prev.includes(nextCursor) ? prev : [...prev, nextCursor],
                      )
                    }
                  >
                    Load older issues
                  </button>
                ) : (
                  <span>· end of list</span>
                )}
              </div>
            </>
          )}
        </div>
        <div className="nx-diagnose__detail-col">
          {selected ? (
            <>
              {selectionIsStale && (
                <p
                  data-testid="diagnose-stale-selection"
                  style={{ margin: '0 0 8px', fontSize: 12, color: 'var(--nx-muted)' }}
                >
                  This issue is no longer in the loaded pages — showing the last known state.
                </p>
              )}
              {/* Keyed by id so switching issues resets the tab and the
                  LogQL box -- while a REFETCH of the same issue (new
                  object, same id) updates in place. The started analysis
                  deliberately lives above this key, so the remount a
                  selection change causes re-attaches to a running job
                  rather than orphaning it. */}
              <IssueDetail
                key={selected.id}
                issue={selected}
                startedAnalysis={analysisJobs.get(selected.id) ?? null}
                onAnalysisStarted={rememberAnalysisJob}
              />
            </>
          ) : selectedId && issuesQuery.isLoading ? (
            <SkeletonLoader shape="card-block" />
          ) : selectedId ? (
            <EmptyState
              title="Issue not loaded"
              description={`Issue ${selectedId} is not in the pages loaded here. Use "Load older issues", or check the link.`}
            />
          ) : (
            <EmptyState title="Select an issue" description="Pick an issue on the left to diagnose it." />
          )}
        </div>
      </div>
    </div>
  );
}
